import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { banners } from '@/mocks/comics';

export default function HeroBanner() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setIndex(((next % banners.length) + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [index]);

  const active = banners[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
    >
      <div className="relative h-[440px] w-full md:h-[560px]">
        {banners.map((banner, i) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={banner.image}
              alt={`${banner.subtitle} 焦点图`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/45 to-foreground-950/20" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
          <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-foreground-950">
            <i className="ri-fire-fill" />
            {active.subtitle}
          </span>
          <h1 className="max-w-2xl font-heading text-3xl leading-tight text-background-50 md:text-5xl">
            {active.slogan}
          </h1>
          <p className="mt-4 max-w-xl text-sm text-background-100/90 md:text-base">
            抖漫为你精选最新与热门漫画，覆盖动作、恋爱、奇幻等十多种题材，每天都有好漫更新。
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${active.comicId}`)}
              className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-play-circle-line text-base" />
              立即阅读
            </button>
            <button
              type="button"
              onClick={() => navigate('/list')}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-background-50/10 cursor-pointer"
            >
              <i className="ri-compass-3-line text-base" />
              发现更多
            </button>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/80 text-foreground-900 transition hover:bg-background-50 cursor-pointer md:left-6"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/80 text-foreground-900 transition hover:bg-background-50 cursor-pointer md:right-6"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {banners.map((banner, i) => (
            <button
              key={banner.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/60 hover:bg-background-50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}